// combatEngine.js — Space Supremacy · TAP-ARENA combat.
// Pure combat math + enemy data. Hero stat blocks and skills are PROFILE-DRIVEN
// (fetched from /api/space/heroes/combat) — there is NO local hero fallback.

import { statMultiplier } from "./stateManager";

const BK = process.env.REACT_APP_BACKEND_URL;
const EIMG = (id) => `${BK}/api/gallery/media/${id}`;

// Encounters keyed by corridor index (0-based). Index 0 is a scavenge cache (no fight).
// `card` binds the Zornelex enemy portrait (Stock Library media id) rendered as a circular frame.
export const ENCOUNTERS = {
  1: [{ key: "skitterer", name: "Skitterer", hp: 120, atk: 16, def: 4, crit: 0.05, atkCd: 2200, card: EIMG("c2e1b599e8d2") }],
  2: [
    { key: "raider_a", name: "Alien Raider", hp: 90, atk: 13, def: 3, crit: 0.05, atkCd: 2400, card: EIMG("ba76aa807d30") },
    { key: "raider_b", name: "Alien Raider", hp: 90, atk: 13, def: 3, crit: 0.05, atkCd: 2700, card: EIMG("ba76aa807d30") },
  ],
  3: [{ key: "elite", name: "Elite Brute", hp: 340, atk: 36, def: 12, crit: 0.08, atkCd: 2000, card: EIMG("a51d7842f5f4") }],
  4: [{ key: "goliath", name: "Alpha Goliath", hp: 820, atk: 30, def: 16, crit: 0.10, atkCd: 1900, sweep: true, card: EIMG("6c61a9781ca2") }],
};

export function encounterFor(index) {
  return ENCOUNTERS[index] || null;
}

// squad = ordered array of deployed hero combat profiles from the backend:
//   { card_id, unit, name, color, atk, def, hp, crit, skills[] }
// Canvas slot = selection order (left column top→bottom).
export function createCombat(squad, squareIndex, level = 1, skinColors = {}) {
  if (!Array.isArray(squad) || squad.length === 0) {
    throw new Error("createCombat: no deployed squad profiles supplied");
  }
  const m = statMultiplier(level);
  const heroes = squad.map((h, i) => {
    if (h == null || h.atk == null || h.hp == null || h.def == null) {
      throw new Error(`createCombat: missing combat stats for hero '${h && h.card_id}'`);
    }
    const maxHp = Math.round(h.hp * m);
    return {
      uid: "h_" + h.card_id, id: h.card_id, side: "hero", name: h.name,
      color: (h.unit && skinColors[h.unit]) || h.color || "#4aa3ff",
      atk: Math.round(h.atk * m), def: h.def, baseDef: h.def, crit: h.crit ?? 0.1,
      hp: maxHp, maxHp, shield: 0, taunt: false, alive: true, slot: i,
      energy: 0, maxEnergy: 100,
      autoCd: 1500, lastAuto: performance.now() + 700 + i * 180, // staggered baseline auto-attacks
      skills: h.skills || [],
    };
  });
  const defs = ENCOUNTERS[squareIndex] || [];
  const isBoss = defs.some((e) => e.key === "goliath");
  // Cinematic Window Guarantee — the instant the Square-5 boss spawns, force every
  // deployed hero's Ultimate meter to a full 100% so all slot-4 finishers unlock.
  if (isBoss) for (const h of heroes) h.energy = h.maxEnergy;
  // Difficulty scaling — the classic trek was balanced for a 2-hero front line.
  // Deploying more heroes scales the Zornelex wave HP/ATK so 5-pad squads still
  // face a real fight (Easy→Lethal band tracks squad size).
  const sqScale = Math.max(1, 1 + 0.32 * (heroes.length - 2));
  const enemies = defs.map((e, i) => ({
    uid: "e_" + i, id: e.key, side: "enemy", name: e.name, color: "#ff5a6a",
    atk: Math.round(e.atk * (1 + 0.12 * (heroes.length - 2))), def: e.def, crit: e.crit || 0.05,
    hp: Math.round(e.hp * sqScale), maxHp: Math.round(e.hp * sqScale), alive: true, slot: i, total: defs.length,
    atkCd: e.atkCd || 2200, lastAtk: performance.now() + 900, stunnedUntil: 0,
    sweep: !!e.sweep, boss: e.key === "goliath", card: e.card || null,
  }));
  return { heroes, enemies, over: false, win: false, boss: isBoss };
}

// Damage with shield absorption. Returns dmg dealt to HP, crit flag, absorbed amount.
export function attackUnit(attacker, defender, mult = 1) {
  let base = Math.max(1, attacker.atk * mult - defender.def);
  const crit = Math.random() < attacker.crit;
  if (crit) base *= 1.5;
  let dmg = Math.round(base);
  let absorbed = 0;
  if (defender.shield > 0) {
    absorbed = Math.min(defender.shield, dmg);
    defender.shield -= absorbed;
    dmg -= absorbed;
  }
  defender.hp = Math.max(0, defender.hp - dmg);
  if (defender.hp === 0) defender.alive = false;
  return { dmg, crit, absorbed };
}

export function healUnit(target, amount) {
  const before = target.hp;
  target.hp = Math.min(target.maxHp, target.hp + amount);
  return { healed: target.hp - before };
}

export const aliveOf = (arr) => arr.filter((u) => u.alive);
export const teamDead = (arr) => arr.length > 0 && arr.every((u) => !u.alive);
export const lowestHp = (arr) => aliveOf(arr).sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp)[0] || null;

